import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  InputAdornment
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  AudioFile as AudioFileIcon,
  CameraAlt as CameraIcon,
  LocationOn as LocationIcon,
  ContactPhone as ContactIcon,
  Poll as PollIcon,
  StickyNote2 as StickerIcon,
  Movie as VideoIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../api';

const SendWhatsAppModal = ({ open, onClose, selectedLeads, refreshLeads }) => {
  const [attachmentType, setAttachmentType] = useState('text'); // text, image, video, audio, document, location, contact, poll, sticker
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);

  // Specific Metadata States
  const [locData, setLocData] = useState({ latitude: '', longitude: '', name: '', address: '' });
  const [contactData, setContactData] = useState({ name: '', phone: '' });
  const [pollData, setPollData] = useState({ question: '', options: ['', ''] });

  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // --- Handlers ---

  const handleAttachClick = (event) => setAnchorEl(event.currentTarget);
  const handleAttachClose = () => setAnchorEl(null);

  const handleSelectType = (type) => {
    setAttachmentType(type);
    setFile(null);
    setAnchorEl(null);
    if (type === 'audio') {
      // Prepare for file select? or just let user record/pick
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const triggerFileSelect = (accept) => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('accept', accept);
      fileInputRef.current.click();
    }
  };

  // --- Recorder Logic ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], "voice_note.webm", { type: 'audio/webm' });
        setFile(audioFile);
        setAttachmentType('audio');
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
    } catch (err) {
      console.error(err);
      alert("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (attachmentType !== 'location' && attachmentType !== 'contact' && attachmentType !== 'poll') {
      setAttachmentType('text');
    }
  };

  const handlePollOptionChange = (index, value) => {
    const newOpts = [...pollData.options];
    newOpts[index] = value;
    setPollData({ ...pollData, options: newOpts });
  };

  const addPollOption = () => {
    setPollData({ ...pollData, options: [...pollData.options, ''] });
  };

  const removePollOption = (index) => {
    const newOpts = pollData.options.filter((_, i) => i !== index);
    setPollData({ ...pollData, options: newOpts });
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('lead_ids', JSON.stringify(selectedLeads));
      formData.append('type', attachmentType);

      // Metadata construction
      let meta = {};
      if (attachmentType === 'location') meta = locData;
      else if (attachmentType === 'contact') meta = contactData;
      else if (attachmentType === 'poll') meta = pollData;

      formData.append('metadata', JSON.stringify(meta));

      if (message) formData.append('message', message);
      if (file) formData.append('file', file);

      await api.post('/admin/whatsapp/bulk-send', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert('Sent successfully!');
      if (refreshLeads) refreshLeads();
      handleClose();

    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to send.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setFile(null);
    setAttachmentType('text');
    setLocData({ latitude: '', longitude: '', name: '', address: '' });
    setContactData({ name: '', phone: '' });
    setPollData({ question: '', options: ['', ''] });
    onClose();
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // --- Render Helpers ---

  const renderInputs = () => {
    switch (attachmentType) {
      case 'location':
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Latitude" value={locData.latitude} onChange={(e) => setLocData({ ...locData, latitude: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Longitude" value={locData.longitude} onChange={(e) => setLocData({ ...locData, longitude: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Location Name" value={locData.name} onChange={(e) => setLocData({ ...locData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address" value={locData.address} onChange={(e) => setLocData({ ...locData, address: e.target.value })} />
            </Grid>
          </Grid>
        );
      case 'contact':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Contact Name" value={contactData.name} onChange={(e) => setContactData({ ...contactData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Phone Number" value={contactData.phone} onChange={(e) => setContactData({ ...contactData, phone: e.target.value })} />
            </Grid>
          </Grid>
        );
      case 'poll':
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField fullWidth label="Poll Question" value={pollData.question} onChange={(e) => setPollData({ ...pollData, question: e.target.value })} />
            <Typography variant="subtitle2">Options</Typography>
            {pollData.options.map((opt, i) => (
              <Box key={i} display="flex" gap={1}>
                <TextField fullWidth size="small" placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => handlePollOptionChange(i, e.target.value)} />
                {pollData.options.length > 2 && (
                  <IconButton size="small" onClick={() => removePollOption(i)}><CloseIcon /></IconButton>
                )}
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addPollOption} size="small">Add Option</Button>
          </Box>
        );
      case 'image':
      case 'video':
      case 'document':
        return (
          <TextField
            multiline rows={2} placeholder="Add a caption..."
            value={message} onChange={(e) => setMessage(e.target.value)}
            fullWidth
          />
        );
      case 'sticker':
        return <Typography variant="caption" color="text.secondary">Sticker selected. No caption needed.</Typography>;
      default: // text or audio
        return !isRecording && (
          <TextField
            multiline rows={4} placeholder="Type message..."
            value={message} onChange={(e) => setMessage(e.target.value)}
            fullWidth
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send WhatsApp - {attachmentType.toUpperCase()}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} minHeight={200} justifyContent="flex-end">

          {file && (
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: '#f5f5f5' }}>
              <Box overflow="hidden"><Typography noWrap>{file.name}</Typography></Box>
              <IconButton size="small" onClick={handleRemoveFile}><DeleteIcon /></IconButton>
            </Paper>
          )}

          {isRecording && (
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} p={2} bgcolor="#ffebee" borderRadius={1}>
              <MicIcon color="error" /> <Typography>Recording {formatTime(recordingTime)}</Typography>
              <IconButton onClick={stopRecording}><StopIcon /></IconButton>
            </Box>
          )}

          {renderInputs()}

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#fafafa', justifyContent: 'space-between' }}>
        <Box>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
          <Tooltip title="Attachments">
            <IconButton onClick={handleAttachClick} disabled={loading || isRecording} color="primary">
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleAttachClose}>
            <MenuItem onClick={() => { handleSelectType('image'); triggerFileSelect('image/*'); }}>
              <ListItemIcon><ImageIcon /></ListItemIcon> Image/Camera
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('video'); triggerFileSelect('video/*'); }}>
              <ListItemIcon><VideoIcon /></ListItemIcon> Video/GIF
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('document'); triggerFileSelect('*/*'); }}>
              <ListItemIcon><AttachFileIcon /></ListItemIcon> Document
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('audio'); triggerFileSelect('audio/*'); }}>
              <ListItemIcon><AudioFileIcon /></ListItemIcon> Audio File
            </MenuItem>
            <MenuItem onClick={() => handleSelectType('location')}>
              <ListItemIcon><LocationIcon /></ListItemIcon> Location
            </MenuItem>
            <MenuItem onClick={() => handleSelectType('contact')}>
              <ListItemIcon><ContactIcon /></ListItemIcon> Contact
            </MenuItem>
            <MenuItem onClick={() => handleSelectType('poll')}>
              <ListItemIcon><PollIcon /></ListItemIcon> Poll
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('sticker'); triggerFileSelect('image/webp'); }}>
              <ListItemIcon><StickerIcon /></ListItemIcon> Sticker
            </MenuItem>
          </Menu>
        </Box>

        <Box>
          {attachmentType === 'text' && !message && !file && !isRecording ? (
            <IconButton onClick={startRecording} color="primary" sx={{ bgcolor: '#e3f2fd' }}><MicIcon /></IconButton>
          ) : (
            <Button variant="contained" onClick={handleSend} disabled={loading || isRecording} endIcon={loading && <CircularProgress size={20} />}>
              Send
            </Button>
          )}
          <Button onClick={handleClose}>Cancel</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SendWhatsAppModal;
